import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import DynamicForm, { DynamicFormRef, RADIO_INPUT_TYPE, setCurrencySymbolForMoneyInputs } from "@mavichpe/dynamic-forms";
import { Table } from "reactstrap";
import CreditAmountFormConfig from "./form";
import { CurrencyApi } from "@/api/CurrencyApi";
import { setCurrency } from "@/store/slices/currencySlides";
import { useAppDispatch } from "@/store/hooks";
import { CurrencyIdPubSub, CurrencyUpdatePubSubPayload } from "@utils/pubsubs/PubSub";
import { CURRENCIES_SYMBOLS } from "@utils/Constants";
import { formatMoneyValue } from "@utils/Currency";
import { removeFunctionFromObject } from "@utils/Utils";
import {
  CREDIT_AMOUNT_TYPE_FIXED,
  CREDIT_AMOUNT_TYPE_PERCENT,
  CreditAmountTarget,
  distributeByBalance,
  resolveCreditAmount
} from "@utils/CreditAmount";
import { CreditAmountFormData } from "@/formTypes/CreditAmountFormDataType";

type CreditAmountFormProps = {
  documents: CreditAmountTarget[];
  currencyId: number;
  onDateChange: (date: string) => void;
  onCreditAmountSubmit: (data: CreditAmountFormData) => void;
};

const CreditAmountForm = forwardRef<DynamicFormRef, CreditAmountFormProps>(
  (props, ref) => {
    const { documents, currencyId, onDateChange, onCreditAmountSubmit } = props;
    const dispatch = useAppDispatch();
    const [currencySymbol, setCurrencySymbol] = useState<string>("");
    const [amountType, setAmountType] = useState<string>(CREDIT_AMOUNT_TYPE_FIXED);
    const [amountValue, setAmountValue] = useState<number>(0);

    const internalRef = useRef<DynamicFormRef>(null);
    useImperativeHandle(ref, () => internalRef.current as DynamicFormRef, []);

    const hasDocuments = documents.length > 0;
    const baseTotal = documents.reduce((a, d) => a + Number(d.saldo || 0), Number(0));
    const resolvedAmount = resolveCreditAmount(amountType, amountValue, baseTotal);

    useEffect(() => {
      const unsubscribe = CurrencyIdPubSub.subscribe(setFormCurrency);
      return () => {
        unsubscribe();
      };
    }, []);

    const setFormCurrency = async (payload: CurrencyUpdatePubSubPayload) => {
      const { currencyId } = payload;
      const priceData = await CurrencyApi.get(currencyId);
      if (priceData.currency_id) {
        dispatch(setCurrency(removeFunctionFromObject(priceData)));
        setCurrencySymbol(CURRENCIES_SYMBOLS[priceData.currency_id])
        internalRef.current?.setFormValue("multiple",
          {
            currencyId,
            exchange_rate: priceData.exchange_rate
          }, "multipleData")
      }
    }

    // La identidad del config debe ser estable: si cambia en cada render el
    // DynamicForm se reinicia y pierde lo que el usuario esta escribiendo.
    const formConfig = useMemo(() => {
      const fields = CreditAmountFormConfig.sections.flatMap(s => s.fields).flat();

      const tipoInput = fields.find(f => f.name === "tipo")
      if (tipoInput) {
        // Sin documentos no hay saldo base contra el cual sacar un porcentaje.
        tipoInput.type = hasDocuments ? RADIO_INPUT_TYPE : "hidden"
      }

      const valorInput = fields.find(f => f.name === "valor")
      if (valorInput) {
        const isPercent = hasDocuments && amountType === CREDIT_AMOUNT_TYPE_PERCENT;
        valorInput.label = isPercent ? "Porcentaje (%)" : "Monto"
        valorInput.type = isPercent ? "number" : "money"
      }

      if (currencySymbol) {
        return setCurrencySymbolForMoneyInputs(CreditAmountFormConfig, currencySymbol)
      }

      return CreditAmountFormConfig
    }, [currencySymbol, hasDocuments, amountType])

    /**
     * El input de monto mantiene su propio formato, asi que se toma el ultimo
     * valor visto por `onInputChange` cuando el submit no lo trae.
     */
    const handleSubmit = (formData: CreditAmountFormData) => {
      const valor = Number(formData?.valor || 0) || amountValue;
      onCreditAmountSubmit({
        ...formData,
        valor
      })
    }

    const onInputChange = (name: keyof CreditAmountFormData, value: any) => {
      if (name === "tipo") {
        setAmountType(`${value}`)
      } else if (name === "valor") {
        setAmountValue(Number(value || 0))
      } else if (name === "fecha") {
        onDateChange(`${value}`)
      }
    }

    const renderAllocationSummary = () => {
      if (!hasDocuments || resolvedAmount <= 0 || resolvedAmount >= baseTotal) {
        return null;
      }

      const allocations = distributeByBalance(resolvedAmount, documents);

      return (
        <Table responsive size="sm" className="mt-3" data-testid="credit-amount-allocation">
          <thead>
            <tr>
              <td>Documento</td>
              <td>Saldo</td>
              <td>Monto a favor</td>
            </tr>
          </thead>
          <tbody>
            {allocations.map(allocation => (
              <tr key={allocation.id}>
                <td>{allocation.kind === "settlement" ? "Liquidacion" : "Factura"} #{allocation.numero}</td>
                <td>{formatMoneyValue(allocation.saldo, currencyId)}</td>
                <td data-testid={`credit-amount-allocation-${allocation.id}`}>
                  {formatMoneyValue(allocation.monto, currencyId)}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )
    }

    return (
      <>
        <DynamicForm<CreditAmountFormData>
          ref={internalRef}
          formConfig={formConfig}
          onInputChange={onInputChange}
          onSubmit={handleSubmit}
        />
        {hasDocuments && (
          <div className="mt-3" data-testid="credit-amount-summary">
            <div>Saldo pendiente seleccionado: <b>{formatMoneyValue(baseTotal, currencyId)}</b></div>
            <div>Monto a favor a registrar: <b>{formatMoneyValue(resolvedAmount, currencyId)}</b></div>
          </div>
        )}
        {renderAllocationSummary()}
      </>
    );
  }
);

export default CreditAmountForm;
